// src/app/app.config.ts
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  Router,
  withComponentInputBinding,
  withHashLocation,
  withViewTransitions,
} from '@angular/router';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { routes } from './app.routes';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withFetch,
} from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { providePrimeNG } from 'primeng/config';
import { HttpInterceptorService } from './services/other/http-interceptor';
import { MessageService, ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToastService } from './services/other/toast';
import { MyPreset } from './themes/theme';
import { AuthService } from './services/other/auth';
import { catchError, of, tap } from 'rxjs';
import { StateService } from './services/other/state';
import { SessionService } from './services/other/session';
import { SessionExpiryService } from './services/other/session-expiry';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { environment } from '../environments/environment.dev';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, '/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withHashLocation(), withViewTransitions(),withComponentInputBinding()),
    provideAnimationsAsync(),
    // UPDATED: Access firebaseConfig from the nested 'firebase' object
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideMessaging(() => getMessaging()),
    // ... (rest of your providers)
    provideHttpClient(
      withFetch(),
      withInterceptorsFromDi()
    ),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    provideAppInitializer(() => {
      const authService = inject(AuthService);
      const stateService = inject(StateService);
      const sessionService = inject(SessionService);
      const sessionExpiry = inject(SessionExpiryService);
      const router = inject(Router);

      if (!sessionService.getSessionToken()) {
        return router.navigate(['/login']).then(() => undefined);
      }

      // A stored token is not proof the session is still valid, so this call
      // doubles as the boot-time session check: a rejected token is caught by
      // the interceptor, which tears the session down and redirects.
      return authService
        .getUserRole()
        .pipe(
          tap((res) => {
            if (res) {
              stateService.userRole.set((res as any[])[0]?.name);
            } else {
              throw new Error('No role');
            }
          }),
          catchError(() => {
            // An expired session has already been redirected (with its
            // returnUrl); navigating again here would clobber that.
            if (sessionExpiry.isExpired()) return of(undefined);
            return router.navigate(['/login']).then(() => undefined);
          })
        )
        .toPromise();
    }),

    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true,
    },
    MessageService,
    ToastService,
    DialogService,
    ConfirmationService,
    DynamicDialogRef,
  ],
};
